import { useEffect, useRef, useState } from "react"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { SMOOTH_LINE_API_URL } from "src/lib/constants"
import useSWR from "swr"
import type {
  ChatRoomStatus,
  DunningCaseStatus,
  DunningChatMessage,
  DunningChatMessagesResponse,
} from "../types"

const chatMessagesUrl = (id: number) =>
  `${SMOOTH_LINE_API_URL}/base_api/smoother/screen/dunning_case_detail/${id}/chat_messages`

const chatStatusUrl = (id: number) =>
  `${SMOOTH_LINE_API_URL}/base_api/smoother/screen/dunning_case_detail/${id}/chat_status`

const awaitingReplyUrl = (id: number) =>
  `${SMOOTH_LINE_API_URL}/base_api/smoother/screen/dunning_case_detail/${id}/awaiting_reply`

const mergeMessages = (...pages: DunningChatMessage[][]) =>
  Array.from(new Map(pages.flat().map((message) => [message.messageId, message])).values())

/**
 * 督促チャット: 既存 LINE 履歴と督促チャット送信分を時系列でマージした全履歴の取得と、
 * 督促チャットからの送信（LINE 配信 + 接触カウント計上）。
 * カウント送信はケース閉鎖 / Hold、全送信は年末年始などのガードで 4xx が返るため、メッセージを表示する。
 * あわせてチャットステータス（chat_rooms.status = meta.chatStatus）の表示と
 * 明示変更（PATCH chat_status）も担う。
 */
export const useDunningChatMessages = (
  id: number | undefined,
  expectedStatus: DunningCaseStatus
) => {
  const { getWithToken, postWithToken, patchWithToken } = useSmoothApi()
  const url = id ? chatMessagesUrl(id) : null

  const swr = useSWR(
    url,
    async (fetchUrl: string) => {
      const response = await getWithToken(fetchUrl)
      return response.data as DunningChatMessagesResponse
    },
    {
      revalidateOnFocus: true,
      // 同じケースを開き直したときも取り直す（_app.tsx の既定 revalidateIfStale: false では
      // 前回表示以降の返信が出ない）
      revalidateIfStale: true,
    }
  )

  const [history, setHistory] = useState<{
    caseId: number
    messages: DunningChatMessage[]
    next: number | null
  } | null>(null)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [olderError, setOlderError] = useState<string | null>(null)
  const currentId = useRef(id)
  currentId.current = id
  const requestVersion = useRef(0)
  const olderRequest = useRef(false)
  useEffect(() => {
    requestVersion.current += 1
    olderRequest.current = false
    setHistory(null)
    setLoadingOlder(false)
    setOlderError(null)
    return () => {
      requestVersion.current += 1
    }
  }, [id])
  // 最新200件が新着でずれても、追加読込済みの範囲との間に欠落を作らない。
  const latestPage = useRef(swr.data?.data)
  latestPage.current = swr.data?.data
  useEffect(() => {
    const messages = swr.data?.data
    if (!messages) return
    setHistory((old) =>
      old && old.caseId === id ? { ...old, messages: mergeMessages(old.messages, messages) } : old
    )
  }, [id, swr.data])
  const nextBefore =
    history != null && history.caseId === id ? history.next : swr.data?.meta?.nextBeforeMessageId
  const loadOlder = async () => {
    if (!id || !nextBefore || olderRequest.current) return
    const version = requestVersion.current
    const headAtRequest = swr.data?.data ?? []
    olderRequest.current = true
    setLoadingOlder(true)
    setOlderError(null)
    try {
      const response = await getWithToken(`${chatMessagesUrl(id)}?before_message_id=${nextBefore}`)
      if (currentId.current !== id || requestVersion.current !== version) return
      const page = response.data as DunningChatMessagesResponse
      setHistory((old) => ({
        caseId: id,
        messages: mergeMessages(
          page.data,
          old?.caseId === id ? old.messages : [],
          headAtRequest,
          latestPage.current ?? []
        ),
        next: page.meta?.nextBeforeMessageId ?? null,
      }))
    } catch {
      if (currentId.current === id && requestVersion.current === version)
        setOlderError("過去の履歴を取得できませんでした。もう一度お試しください。")
    } finally {
      if (currentId.current === id && requestVersion.current === version) {
        olderRequest.current = false
        setLoadingOlder(false)
      }
    }
  }

  const [isSending, setIsSending] = useState(false)
  const [sendError, setSendError] = useState<string | null>(null)
  const pendingOperation = useRef<{
    dunningCaseId: number
    body: string
    countsAsContact: boolean
    operationKey: string
  } | null>(null)

  /**
   * @param options.countsAsContact false のとき「架電内容の事務連絡」として送信する。
   *   サーバは非カウントの接触ログを作り、督促回数には数えない（当日3回上限ゲートも適用されない）。
   *   省略時は true（通常の督促接触としてカウント）。
   */
  const sendMessage = async (
    body: string,
    options?: { countsAsContact?: boolean }
  ): Promise<boolean> => {
    if (!id) return false
    const countsAsContact = options?.countsAsContact !== false
    const operation =
      pendingOperation.current?.dunningCaseId === id &&
      pendingOperation.current.body === body &&
      pendingOperation.current.countsAsContact === countsAsContact
        ? pendingOperation.current
        : { dunningCaseId: id, body, countsAsContact, operationKey: crypto.randomUUID() }
    pendingOperation.current = operation

    setIsSending(true)
    setSendError(null)
    try {
      const response = await postWithToken(chatMessagesUrl(id), {
        body,
        // 省略時 true 扱いのため、事務連絡（false）のときだけ明示的に送る
        ...(options?.countsAsContact === false ? { countsAsContact: false } : {}),
        expectedStatus,
        operationKey: operation.operationKey,
      })
      // 送信後の最新ページを反映し、追加読込済みの過去履歴は保持する
      await swr.mutate(response.data as DunningChatMessagesResponse, { revalidate: false })
      // 並行中に別本文の送信が始まっていた場合、その新しいキーは残す
      if (pendingOperation.current === operation) pendingOperation.current = null
      return true
    } catch (e) {
      const message =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        "送信に失敗しました。"
      setSendError(message)
      return false
    } finally {
      setIsSending(false)
    }
  }

  const [isUpdatingChatStatus, setIsUpdatingChatStatus] = useState(false)
  const [chatStatusError, setChatStatusError] = useState<string | null>(null)
  const [isUpdatingAwaitingReply, setIsUpdatingAwaitingReply] = useState(false)
  const [updateAwaitingReplyError, setUpdateAwaitingReplyError] = useState<string | null>(null)

  const setAwaitingReplyActive = async (active: boolean, messageId?: number): Promise<boolean> => {
    if (!id) return false
    setIsUpdatingAwaitingReply(true)
    setUpdateAwaitingReplyError(null)
    try {
      const response = await patchWithToken(awaitingReplyUrl(id), {
        active,
        ...(!active && messageId != null ? { messageId } : {}),
      })
      const chatStatus = (response.data as { chatStatus?: ChatRoomStatus }).chatStatus
      if (chatStatus) {
        await swr.mutate(
          (current) => (current ? { ...current, meta: { ...current.meta, chatStatus } } : current),
          { revalidate: false }
        )
      }
      return true
    } catch (e) {
      const message =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        "返信不要として完了できませんでした。"
      setUpdateAwaitingReplyError(message)
      return false
    } finally {
      setIsUpdatingAwaitingReply(false)
    }
  }

  /** チャットステータスの明示変更（set 方式）。成功時はキャッシュの meta だけ差し替える */
  const updateChatStatus = async (status: ChatRoomStatus): Promise<boolean> => {
    if (!id) return false
    setIsUpdatingChatStatus(true)
    setChatStatusError(null)
    try {
      const response = await patchWithToken(chatStatusUrl(id), { status })
      const chatStatus = (response.data as { chatStatus: ChatRoomStatus }).chatStatus
      await swr.mutate(
        (current) => (current ? { ...current, meta: { ...current.meta, chatStatus } } : current),
        { revalidate: false }
      )
      return true
    } catch (e) {
      const message =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        "チャットステータスの変更に失敗しました。"
      setChatStatusError(message)
      return false
    } finally {
      setIsUpdatingChatStatus(false)
    }
  }

  // 再取得・送信後も既に読んだ過去ページを保持し、重複は最新レスポンスを優先する。
  const byId = new Map<number, DunningChatMessage>()
  for (const message of history != null && history.caseId === id ? history.messages : [])
    byId.set(message.messageId, message)
  for (const message of swr.data?.data ?? []) byId.set(message.messageId, message)
  const messages = Array.from(byId.values()).sort(
    (a, b) => (a.sentAt ?? "").localeCompare(b.sentAt ?? "") || a.messageId - b.messageId
  )
  const chatStatus: ChatRoomStatus | null = swr.data?.meta?.chatStatus ?? null

  return {
    messages,
    hasOlder: Boolean(nextBefore),
    loadOlder,
    loadingOlder,
    olderError,
    chatStatus,
    isLoading: Boolean(url) && !swr.data && !swr.error,
    error: swr.error,
    refresh: () => swr.mutate(),
    sendMessage,
    isSending,
    sendError,
    clearSendError: () => setSendError(null),
    updateChatStatus,
    isUpdatingChatStatus,
    chatStatusError,
    setAwaitingReplyActive,
    isUpdatingAwaitingReply,
    updateAwaitingReplyError,
  }
}
